import { 
    gql, 
    useQuery 
} from "@apollo/client";
import { 
  FindMerchantInterestsQueryQuery, 
  QueryFindMerchantInterestsArgs
} from "../../../../../schemaTypes";
import { useCallback } from "react";

export const findMerchantInterestsQuery = gql`
  query FindMerchantInterestsQuery(
    $page: Int!
    $pageSize: Int!
    $id: String!
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findMerchantInterests(
      page: $page
      pageSize: $pageSize
      id: $id
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      companyName
      username
      shortId
      totalInterests
      data{
        customerName
        customerUsername 
        location
        totalSpend
        community{
          id
          name
        }
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindMerchantInterests {
  companyName: FindMerchantInterestsQueryQuery["findMerchantInterests"]["companyName"],
  username: FindMerchantInterestsQueryQuery["findMerchantInterests"]["username"],
  shortId: FindMerchantInterestsQueryQuery["findMerchantInterests"]["shortId"],
  totalInterests: FindMerchantInterestsQueryQuery["findMerchantInterests"]["totalInterests"],
  data: FindMerchantInterestsQueryQuery["findMerchantInterests"]["data"]
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreInterests: boolean;
}

interface Props {
  variables: QueryFindMerchantInterestsArgs;
  children: (data: WithFindMerchantInterests) => React.ReactElement | null;
}

export const FindMerchantInterestsController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    FindMerchantInterestsQueryQuery,
    QueryFindMerchantInterestsArgs
  >(findMerchantInterestsQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const companyName = data?.findMerchantInterests?.companyName || "";
  const username = data?.findMerchantInterests?.username || "";
  const shortId = data?.findMerchantInterests?.shortId || "";
  const totalInterests = data?.findMerchantInterests?.totalInterests || 0;
  const findMerchantInterestsData = data?.findMerchantInterests?.data || [];
  const page = data?.findMerchantInterests?.page || variables.page;
  const pageSize = data?.findMerchantInterests?.pageSize || variables.pageSize;
  const total = data?.findMerchantInterests?.total || 0;
  const totalPages = data?.findMerchantInterests?.totalPages || 0;
  const hasMoreInterests = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreInterests) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findMerchantInterests) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findMerchantInterests: {
            ...fetchMoreResult?.findMerchantInterests,
            data: [
              ...(prev?.findMerchantInterests?.data || []),
              ...(fetchMoreResult?.findMerchantInterests?.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMoreInterests]);

  return children({
    companyName,
    username,
    shortId,
    totalInterests,
    data: findMerchantInterestsData as any,
    loading,
    loadMore,
    hasMoreInterests,
    page,
    pageSize,
    total,
    totalPages,
  });
};